import { BookingServiceClient, IMakeBookingArguments } from 'fitbeat.api-clients';
import {
  AreaCategory,
  BookingSteps,
  buildDayForBooking,
  CardioOrResistance,
  HomeTimeSlot,
  IDisplayableUserProfile, ISelectStudioClassTimeData,
  IUserRecommendedSettings,
  Level,
  OpenSlotForWorkoutType,
  ProcessTypes,
  UserProfile,
  UserWorkoutSession,
  WaitlistEntry,
  WorkoutSettings,
} from 'fitbeat.gym-core';
import {
  BaseUserProfile, EnabledFeatures, Gym, GymTypes, IDay, IHeartRate, WorkoutType,
} from 'fitbeat.models';
import moment from 'moment-timezone';
import { Platform } from 'react-native';
import { Dispatch } from 'redux';
import { UserServiceClient } from 'fitbeat.api-clients/lib/UserServiceClient';
import { WaitingListServiceClient } from 'fitbeat.api-clients/lib/WaitingListServiceClient';
import { Equipment } from 'fitbeat.models/src/workout/equipment';
import { enabledFeaturesSelector, PaywallPage } from '../auth';
import { getConfig } from '../config';
import { IAppStore, startStudioWatchAppWorkout } from '../HomeWorkouts';
import { openPaywallModalAction } from '../payments/redux/payments-actions';
import { openGymWebpage } from '../payments/utils';
import { influencerScheduleTilePressed, PARTNER_BOOKING_UPDATED } from '../Planner/plannerActions';
import { selectWorkoutInviteId } from '../Planner/plannerSelectors';
import { GoogleAnalytics, GymResolver, WorkoutSessionService } from '../services';
import { analyicsForAction, IAnalyticsForAction } from '../services/google-analytics/analyicsForAction';
import { toggleBookingsModal } from '../ui/redux/uiActions';
import { getWorkoutDurationDescription, i18n, Logger } from '../utils';
import { createBookingServiceClient, createUserServiceClient } from '../utils/apiClientFactory';
import { filterInvitesForDay, filterWaitingListForDay } from '../utils/BookingUtils';
import { getTimezoneFromGymName } from '../utils/gyms';
import { Permissions } from '../utils/Permissions';
import { isDesktop } from '../utils/ScreenSizes';
import * as types from './actionTypes';
import { IStudioSlots } from './reducers';
import { PersonaliseSetupScreens } from './steps/personaliseWorkoutSteps/types';
import { openLink } from '../utils/linkUtils';

export const getNextBookingStep = (
  userProfile,
  processType?,
  allBookings?,
  isSoloWorkout?,
  fromPlanner?,
  isFirstWorkout?,
) => async (dispatch, getState) => {
  const state = getState();
  const { enabledFeatures } = state.auth.user;
  const workoutInviteId = selectWorkoutInviteId(state);
  if (isSoloWorkout === undefined) {
    isSoloWorkout = state.bookings.bookingWizard.isSoloWorkout;
  }
  const { currentBookingStep } = state.bookings.bookingWizard;
  dispatch({
    type: types.GET_NEXT_BOOKING_STEP,
    payload: {
      userProfile,
      processType,
      allBookings,
      workoutInviteId,
      isSoloWorkout,
      enabledFeatures,
      fromPlanner,
      isFirstWorkout,
    },
    ...analyicsForAction(types.GET_NEXT_BOOKING_STEP, { currentBookingStep }),
  });
};

export const setupInstantWorkout = (gymName: string, day: IDay) => ({
  type: types.SETUP_INSTANT_WORKOUT,
  payload: {
    gymName,
    day,
  },
});

export const setDayTemp = (day: IDay) => ({
  type: types.SET_DAY_TEMP,
  payload: {
    day,
  },
  ...analyicsForAction('dashboard_workout_now'),
});

export const workoutDateTimeSelected = (date: Date) => ({
  type: types.DATE_TIME_SELECTED,
  payload: {
    date,
  },
});

export const submitWorkoutRating = (rating, comment) => ({
  type: types.SUBMIT_WORKOUT_RATING,
  ...analyicsForAction(types.SUBMIT_WORKOUT_RATING, { rating, comment }),
});

export const startBookingFromWorkouts = () => ({
  type: types.START_BOOKING_FROM_WORKOUTS,
  ...analyicsForAction(types.START_BOOKING_FROM_WORKOUTS),
});

export const viewExistingBooking = () => ({
  type: types.OPEN_EXISTING_BOOKINGS_VIEW,
  ...analyicsForAction(types.OPEN_EXISTING_BOOKINGS_VIEW),
});

export const openWorkoutHistory = () => ({
  type: types.OPEN_WORKOUT_HISTORY,
  ...analyicsForAction(types.OPEN_WORKOUT_HISTORY),
});

export const joinWorkout = () => ({
  type: types.WORKOUT_JOIN,
  ...analyicsForAction(types.WORKOUT_JOIN),
});

export const getPreviousBookingStep = () => ({
  type: types.GET_PREVIOUS_BOOKING_STEP,
  payload: {},
});

export const setIsSoloWorkout = (isSoloWorkout) => ({
  type: types.SET_SOLO_WORKOUT,
  payload: {
    isSoloWorkout,
  },
});

export const setBookingStep = (currentBookingStep) => ({
  type: types.GET_NEXT_BOOKING_STEP,
  payload: {
    currentBookingStep,
  },
});

export const setIsCustomisingWorkout = (isCustomisingWorkout) => ({
  type: types.SET_IS_CUSTOMISING_WORKOUT,
  payload: {
    isCustomisingWorkout,
  },
});

export const setWorkoutSession = (workoutSession) => ({
  type: types.SET_WORKOUT_SESSION,
  payload: {
    workoutSession,
  },
});

export const cancelBookingConfirmed = (
  workoutSessions: UserWorkoutSession[],
) => ({
  type: types.CANCEL_BOOKING_CONFIRMED,
  payload: {
    workoutSessions,
  },
  ...analyicsForAction(types.CANCEL_BOOKING_CONFIRMED),
});

const waitlistBookingRequest = () => ({
  type: types.WAITINGLIST_BOOKING_REQUEST,
  ...analyicsForAction(types.WAITINGLIST_BOOKING_REQUEST),
});

const waitlistBookingSuccess = (userProfile: UserProfile) => async (dispatch, getState: () => IAppStore) => {
  const state = getState();
  const enabledFeatures = enabledFeaturesSelector(state);
  dispatch({
    type: types.WAITINGLIST_BOOKING_SUCCESS,
    payload: {
      userProfile,
      enabledFeatures,
    },
    ...analyicsForAction(types.WAITINGLIST_BOOKING_SUCCESS),
  });
};

const waitlistBookingFailure = (error) => ({
  type: types.WAITINGLIST_BOOKING_FAILURE,
  payload: {
    error,
  },
});

export interface ICancelBookingConfirmed extends IAnalyticsForAction {
  type: typeof types.CANCEL_BOOKING_CONFIRMED;
  payload: {
    workoutSessions: UserWorkoutSession[];
  };
}

export const setSelectedGym = (currentSelectedGymName, atStudio?: boolean) => {
  const currentSelected = currentSelectedGymName
    ? GymResolver.getByName(currentSelectedGymName)
    : null;
  const isHomeSelected = !!(currentSelected && currentSelected.type === GymTypes.Home);
  function getEventName() {
    if (isHomeSelected && atStudio) {
      return 'book_location_office';
    }
    if (isHomeSelected) {
      return 'book_location_home';
    }
    return 'book_location_studio';
  }

  return {
    type: types.SET_SELECTED_GYM,
    payload: {
      currentSelectedGymName,
      atStudio,
    },
    ...analyicsForAction(
      getEventName(),
      { gym_selected: currentSelectedGymName },
    ),
  };
};

export const setSoloWorkout = (isSoloWorkout: boolean, userProfile?: BaseUserProfile) => ({
  type: types.SET_SOLO_WORKOUT,
  payload: {
    isSoloWorkout,
    userProfile,
  },
  ...analyicsForAction(isSoloWorkout ? 'book_home_friends_no' : 'book_home_friends_yes'),
});

export const setInvites = (invites) => ({
  type: types.SET_INVITES,
  payload: {
    invites,
  },
});

export const setPartnerProfiles = (partnerProfiles) => ({
  type: types.SET_PARTNER_PROFILES,
  payload: {
    partnerProfiles,
  },
});

export const setWaitlistEntry = (waitlistEntry) => ({
  type: types.SET_WAITLIST_ENTRY,
  payload: {
    waitlistEntry,
  },
});

export const setLongTermGoal = (longTermGoal) => ({
  type: types.SET_LONG_TERM_GOAL,
  payload: {
    longTermGoal,
  },
});

export const fetchPartnerProfilesRequest = () => ({
  type: types.FETCH_PARTNER_PROFILES_REQUEST,
  payload: {},
});

export const fetchPartnerProfilesSuccess = (invites, partnerProfiles) => ({
  type: types.FETCH_PARTNER_PROFILES_SUCCESS,
  payload: {
    invites,
    partnerProfiles,
  },
});

export const fetchPartnerProfilesFailed = (error) => ({
  type: types.FETCH_PARTNER_PROFILES_FAILED,
  payload: {
    error,
  },
});

export const shareInvite = () => ({
  type: types.SHARE_BOOKING_INVITE,
  ...analyicsForAction(types.SHARE_BOOKING_INVITE),
});

export const cancelBookingRequest = () => ({
  type: types.CANCEL_BOOKING_REQUEST,
  ...analyicsForAction(types.CANCEL_BOOKING_REQUEST),
});

export const skipInvite = () => ({
  type: types.SKIP_BOOKING_INVITE,
  ...analyicsForAction(types.SKIP_BOOKING_INVITE),
});

export const bookingInviteContinue = (shareLink, workoutSessionId) => ({
  type: types.BOOKING_INVITE_CONTINUE,
  payload: {
    shareLink,
    workoutSessionId,
  },
  ...analyicsForAction(types.BOOKING_INVITE_CONTINUE),
});

export interface IBookingInviteContinue {
  type: typeof types.BOOKING_INVITE_CONTINUE;
  payload: {
    shareLink: string | null;
    workoutSessionId: string
  };
}

export const fetchPartnerProfiles = (invites, getUserProfileDetails) => async (dispatch) => {
  dispatch(fetchPartnerProfilesRequest());
  try {
    const partnerProfiles = {};

    for (const invite of invites) {
      const partnerProfile = await getUserProfileDetails(invite.pairedSessionUserProfileId);
      partnerProfiles[invite.uniqueReference] = partnerProfile;
    }

    dispatch(fetchPartnerProfilesSuccess(invites, partnerProfiles));
  } catch (error) {
    dispatch(fetchPartnerProfilesFailed(error));
  }
};

export const upgradeStudioPlanPressed = (userProfile: UserProfile) => (
  async (dispatch) => {
    try {
      dispatch({ type: types.UPGRADE_STUDIO_PLAN_PRESSED });
      const userServiceClient = createUserServiceClient();
      const openLinkInExternalBrowser = Platform.OS === 'web';
      if (openLinkInExternalBrowser) {
        const portalSessionForBrowser = await userServiceClient.getCustomerPortalSession({ returnToBilling: false });
        await openLink(portalSessionForBrowser.url);
        const upgradePressedSuccess = () => {
          closeBookingWizard(dispatch, userProfile);
          dispatch({ type: types.UPGRADE_STUDIO_PLAN_PRESSED_SUCCESS });
        };
        const timeoutToShowLoadingWhileBrowserOpensPortal = 2000;
        setTimeout(upgradePressedSuccess, timeoutToShowLoadingWhileBrowserOpensPortal);
      } else {
        const portalSessionForModal = await userServiceClient.getCustomerPortalSession({ useReturnToAppURL: false });
        closeBookingWizard(dispatch, userProfile);
        dispatch({
          type: types.UPGRADE_STUDIO_PLAN_PRESSED_SUCCESS,
          payload: {
            accountSelfManagementLink: portalSessionForModal.url,
          },
        });
      }
    } catch (error) {
      Logger.notify(error);
      dispatch({ type: types.UPGRADE_STUDIO_PLAN_PRESSED_FAILED });
    }
  }
);

export const resetWizard = (userProfile: BaseUserProfile) => ({
  type: types.RESET_WIZARD,
  payload: { userProfile },
});

export interface ILiveStreamOptionsToggleValues {
  preWarmupTalkTimeToggleValue: boolean;
  livestreamToggleValue: boolean;
}

export const liveStreamOptionToggled = () => ({
  type: types.LIVE_STREAM_OPTION_TOGGLED,
});

export const preWarmupTalkTimeToggled = () => ({
  type: types.PRE_WARMUP_TALK_TIME_OPTION_TOGGLED,
});

export const willDropOffAfterTalkToggled = () => ({
  type: types.WILL_DROP_OFF_AFTER_TALK_TOGGLED,
});

export const liveStreamOptionsNextPressed = (
  userProfile: BaseUserProfile,
  preWorkoutTalkTopic?: string,
) => (
  (dispatch) => {
    dispatch({
      type: types.LIVE_STREAM_OPTIONS_NEXT_PRESSED,
      payload: {
        preWorkoutTalkTopic,
      },
    });
    dispatch(getNextBookingStep(userProfile));
  });

export const getOpenInviteClassList = (bookingServiceClient, workoutId, hideLoader) => async (dispatch) => {
  dispatch(getOpenInviteClassListRequest(workoutId, hideLoader));
  try {
    const openInviteUserProfiles = await bookingServiceClient.getOpenInviteClassList(workoutId);
    dispatch(getOpenInviteClassSuccess(workoutId, openInviteUserProfiles));
  } catch (error) {
    dispatch(getOPenInviteClassFailed(workoutId, (error as Error).message));
  }
};

export const getOpenInviteClassListRequest = (workoutId, hideLoader) => ({
  type: types.GET_CLASS_LIST_REQUEST,
  payload: {
    workoutId,
    hideLoader,
  },
});

export const getOpenInviteClassSuccess = (workoutId, openInviteUserProfiles) => ({
  type: types.GET_CLASS_LIST_SUCCESS,
  payload: {
    workoutId,
    openInviteUserProfiles,
  },
});

export const getOPenInviteClassFailed = (workoutId, error) => ({
  type: types.GET_CLASS_LIST_FAILURE,
  payload: {
    workoutId,
    error,
  },
});

const existingBookingForDay = (studioSlots: IStudioSlots): boolean => {
  for (const slot of studioSlots.fiftyMinuteWorkoutSlots) {
    if (slot.booked) {
      return true;
    }
  }
  for (const slot of studioSlots.thirtyMinuteWorkoutSlots) {
    if (slot.booked) {
      return true;
    }
  }
  return false;
};

const createDemoStudioBooking = async (
  workoutSessionService: WorkoutSessionService,
  day: IDay,
  userProfile: UserProfile,
  gym: Gym,
  dispatch,
  state: IAppStore,
) => {
  dispatch({ type: types.CREATE_DEMO_STUDIO_BOOKING_REQUEST });
  try {
    const {
      pendingSlot, currentSelectedGymName, recommendedSettings, studioSlots, areaCategory,
    } = state.bookings.bookingWizard;
    let workoutSession;
    if (existingBookingForDay(studioSlots)) {
      const bookingServiceClient = createBookingServiceClient();
      const bookingsForDay = (await bookingServiceClient.getBookingsForDay(pendingSlot.startTime));
      // Assumes that only one booking per day allowed
      const existingBooking = bookingsForDay[0];
      workoutSession = await workoutSessionService.changeBooking(
        existingBooking._id,
        pendingSlot.startTime,
        pendingSlot.workoutType,
        currentSelectedGymName,
        recommendedSettings,
        true, // isStudioDemo
      );
    } else {
      workoutSession = await workoutSessionService.makeBooking(
        false,
        pendingSlot,
        currentSelectedGymName,
        recommendedSettings,
        true,
        getConfig().workoutPlaybackSpeed,
        null,
        null,
        areaCategory,
      );
    }
    dispatch(setWorkoutSession(workoutSession));
    dispatch(getNextBookingStep(userProfile));

    dispatch({ type: types.CREATE_DEMO_STUDIO_BOOKING_SUCCESS });
  } catch (error) {
    dispatch({ type: types.CREATE_DEMO_STUDIO_BOOKING_FAILURE, payload: { errorMessage: (error as Error).message } });
  }
};

const handleWaitinglist = async (
  dispatch,
  pendingSlot: any,
  waitingListServiceClient: WaitingListServiceClient,
  userProfile: UserProfile,
  gym: Gym,
) => {
  dispatch({ type: types.WAITINGLIST_REQUEST });
  try {
    let waitinglistEntry: WaitlistEntry | undefined;

    if (pendingSlot.inWaitinglist) {
      const waitlistUid = WaitlistEntry.GenerateUID(pendingSlot, userProfile.uid, gym.name);
      waitinglistEntry = await waitingListServiceClient.getWaitingListEntry(waitlistUid);
    } else if (pendingSlot.isFull) {
      waitinglistEntry = await waitingListServiceClient.addToWaitingList(pendingSlot, gym.name);
    }

    const waitlistEntries = await waitingListServiceClient.getWaitingList();

    dispatch(setWaitlistEntry(waitinglistEntry));
    dispatch(getNextBookingStep(userProfile));
    dispatch({
      type: types.WAITINGLIST_SUCCESS,
      payload: { waitlistEntries },
    });
  } catch (error) {
    dispatch({ type: types.WAITINGLIST_FAILURE, payload: { errorMessage: (error as Error).message } });
  }
};

export const selectDateTimeNextClicked = (
  waitingListServiceClient: WaitingListServiceClient,
  workoutSessionService: WorkoutSessionService,
  day: IDay,
  userProfile: UserProfile,
  gym: Gym,
) => async (dispatch, getState) => {
  const preBookingState = getState();
  const { pendingSlot } = preBookingState.bookings.bookingWizard;

  if (pendingSlot.inWaitinglist || pendingSlot.isFull) {
    return handleWaitinglist(dispatch, pendingSlot, waitingListServiceClient, userProfile, gym);
  }

  if (preBookingState.bookings.bookingWizard.processType === ProcessTypes.DemoStudioBooking) {
    return createDemoStudioBooking(workoutSessionService, day, userProfile, gym, dispatch, preBookingState);
  }

  if (gym.type === GymTypes.Indoor) {
    dispatch(getNextBookingStep(userProfile));
    return;
  }

  await Promise.all([
    getRecommendedSettings(workoutSessionService, userProfile, day)(dispatch, getState),
    getBookingSlots(
      dispatch,
      getState,
      workoutSessionService,
      day,
      gym,
    ),
  ]);

  const state = getState();
  const { bookingSlotsError } = state.bookings.bookingWizard;
  if (!bookingSlotsError) {
    dispatch(getNextBookingStep(userProfile));
  }
};

export const getBookingSlotsAndRecommendedSettings = (
  workoutSessionService: WorkoutSessionService,
  day: IDay,
  userProfile: UserProfile,
  gym: Gym,
) => async (dispatch, getState) => {
  getBookingSlotsInBackground(
    dispatch,
    getState,
    workoutSessionService,
    day,
    gym,
  );
  dispatch(getRecommendedSettings(workoutSessionService, userProfile, day));
};

export const loadSelectStartTimeScreen = (
  workoutSessionService: WorkoutSessionService,
  day: IDay,
  userProfile: UserProfile,
  gym: Gym,
) => async (dispatch, getState) => {
  getBookingSlotsInBackground(
    dispatch,
    getState,
    workoutSessionService,
    day,
    gym,
  );
  dispatch(getRecommendedSettings(workoutSessionService, userProfile, day));
  dispatch(setPendingSlot(null, undefined));
  dispatch(setWaitlistEntry(undefined));
};

const getBookingSlotsInBackground = (
  dispatch,
  getState,
  workoutSessionService: WorkoutSessionService,
  day,
  gym: Gym,
) => {
  getBookingSlots(dispatch, getState, workoutSessionService, day, gym)
    .catch((e) => { /* handled in getBookingSlots */
    });
};

export const getBookingSlots = async (
  dispatch,
  getState,
  workoutSessionService: WorkoutSessionService,
  day,
  gym: Gym,
) => {
  dispatch(getBookingSlotsRequest());
  try {
    if (gym.type === GymTypes.Home) {
      await getHomeSlots(dispatch, workoutSessionService, day, gym.name);
    } else {
      const state: IAppStore = getState();
      const { isSoloWorkout } = state.bookings.bookingWizard;

      let withPartner = !isSoloWorkout;
      if (!getConfig().friendInviteEnabledForStudio) {
        withPartner = false;
      }

      await getStudioZoneSlots(dispatch, day, gym.name, withPartner);
    }
  } catch (error) {
    const { message } = (error as Error);
    if (!message.match(i18n.t('bookings:noSlotsAvailable'))) {
      Logger.notify(error);
    }
    dispatch(getBookingSlotsFailed((error as Error).message));
  }
};

const getHomeSlots = async (
  dispatch,
  workoutSessionService: WorkoutSessionService,
  day: IDay,
  gymName: string,
) => {
  const date = day.isToday ? moment() : day.date;
  const from = moment(date).valueOf();
  const homeSlots = await workoutSessionService.getHomeAvailableBookings(
    from,
    gymName,
  );

  if (homeSlots.length > 0) {
    dispatch(getHomeBookingSlotSuccess(homeSlots));
  } else {
    throw new Error(i18n.t('noSlotsAvailable'));
  }
};

const getStudioZoneSlots = async (
  dispatch,
  day: IDay,
  gymName: string,
  withPartner: boolean,
) => {
  const bookingServiceClient = createBookingServiceClient();
  const yyyymmdd = moment(day.date).format('YYYYMMDD');
  const selectClassTimeData = await bookingServiceClient.getAvailableZoneBookingsOnYYYYMMDD(
    withPartner,
    yyyymmdd,
    gymName,
  );

  Logger.leaveBreadcrumb('Get Available Bookings', {
    type: 'request',
    component: '<Booking />',
    withPartner,
    yyyymmdd,
  });

  if (selectClassTimeData.length > 0) {
    const gymTimezone = getTimezoneFromGymName(gymName);
    dispatch(getStudioBookingSlotSuccess(selectClassTimeData, gymTimezone, new Date()));
  } else {
    throw new Error(i18n.t('bookings:noSlotsAvailable'));
  }
};

export const getBookingSlotsRequest = () => ({
  type: types.GET_BOOKING_SLOTS_REQUEST,
});

export const clearLoadedSlots = () => ({
  type: types.CLEAR_LOADED_SLOTS,
});

const getHomeBookingSlotSuccess = (homeSlots: HomeTimeSlot) => ({
  type: types.GET_HOME_BOOKING_SLOTS_SUCCESS,
  payload: { homeSlots },
});

export const getStudioBookingSlotSuccess = (
  selectClassTimeData: ISelectStudioClassTimeData,
  gymTimezone: string,
  now: Date,
) => ({
  type: types.GET_STUDIO_BOOKING_SLOTS_SUCCESS,
  payload: {
    selectClassTimeData,
    gymTimezone,
    now,
  },
});

export const getBookingSlotsFailed = (error: string) => ({
  type: types.GET_BOOKING_SLOTS_FAILURE,
  payload: { error },
});

export const setPendingSlot = (pendingSlot, areaCategory: AreaCategory | undefined) => async (dispatch, getState) => {
  const state = getState();
  const { currentSelectedGymName } = state.bookings.bookingWizard;
  const gymType = gymTypeAnalyticsDescription(currentSelectedGymName);
  dispatch({
    type: types.SET_PENDING_SLOT,
    payload: { pendingSlot, areaCategory },
    ...analyicsForAction(
      pendingSlot && pendingSlot.isInstant
        ? `book_${gymType}_time_now`
        : `book_${gymType}_time_shedule`,
    ),
  });
};

export const onDemandSlotSelectedAction = (startTime: Date, workoutType: WorkoutType) => async (dispatch) => {
  dispatch(setPendingSlot({ startTime, workoutType }, undefined));
};

export const onDemandSlotSelectBackPressAction = () => (
  (dispatch) => {
    dispatch(setPendingSlot(null, undefined));
    dispatch(getPreviousBookingStep());
    dispatch({ type: types.ON_DEMAND_STUDIO_SELECT_SLOT_BACK_PRESSED });
  }
);

function updateWorkoutSettings(workoutId: string, newSettings: WorkoutSettings) {
  const bookingServiceClient = createBookingServiceClient();
  return bookingServiceClient.resetWorkoutSettings(
    workoutId,
    newSettings,
    false,
    false,
  );
}

const shouldRequestBagContactSetting = (workoutSession: UserWorkoutSession): boolean => {
  const gym = GymResolver.getByName(workoutSession.gymName);
  return !!(
    gym.bagContactAllowed
    && workoutSession.settings.cardioOrResistance !== CardioOrResistance.Balance
  );
};

export const checkInOnDemandBookingPressedAction = (userProfile: UserProfile) => (
  async (dispatch, getState: () => IAppStore) => {
    try {
      dispatch({ type: types.CHECK_IN_ON_DEMAND_BOOKING_PRESS_REQUEST });
      const {
        planner: { workoutSessions: plannerWorkoutSessions },
        bookings: { bookingWizard: { workoutSession: bookingWizardWorkoutSession } },
      } = getState();
      if (!bookingWizardWorkoutSession) {
        throw new Error('Expected workout session to already be booked and recommended settings to exist');
      }

      const updatedWorkoutSessionInPlanner = plannerWorkoutSessions.find(
        (session) => session.uniqueReference === bookingWizardWorkoutSession?.uniqueReference,
      );
      const workoutSessionToCheckIn = updatedWorkoutSessionInPlanner || bookingWizardWorkoutSession;

      dispatch(setWorkoutSession(workoutSessionToCheckIn));

      if (shouldRequestBagContactSetting(workoutSessionToCheckIn)) {
        dispatch(getNextBookingStep(userProfile, ProcessTypes.BagContact));
      } else {
        dispatch(getNextBookingStep(userProfile, ProcessTypes.CheckIn));
        await checkUserIn(dispatch, workoutSessionToCheckIn);
      }
      dispatch({ type: types.CHECK_IN_ON_DEMAND_BOOKING_PRESS_SUCCESS });
    } catch (e) {
      dispatch({ type: types.CHECK_IN_ON_DEMAND_BOOKING_PRESS_FAILURE });
    }
  }
);

const getSelectedGymOrThrow = (state: IAppStore): string => {
  const { bookings: { bookingWizard: { currentSelectedGymName } } } = state;
  if (!currentSelectedGymName) {
    throw new Error('gym name required to get on demand slots');
  }
  return currentSelectedGymName;
};

export interface IStartOnDemandWorkoutPressedSuccess {
  type: typeof types.START_ON_DEMAND_STUDIO_BOOKING_SUCCESS;
  payload: {
    booking: UserWorkoutSession;
  }
}

export const startOnDemandWorkoutPressedAction = (userProfile: BaseUserProfile) => (
  async (dispatch, getState: () => IAppStore) => {
    try {
      dispatch({ type: types.START_ON_DEMAND_STUDIO_BOOKING_REQUEST });
      const { bookings: { bookingWizard: bookingWizardState } } = getState();
      const { startTime, workoutType } = bookingWizardState.pendingSlot;
      const { areaCategory } = bookingWizardState;
      if (!startTime || !workoutType || !bookingWizardState.currentSelectedGymName) {
        throw new Error('startTime, workoutType and gymName are required fields to create a on demand booking');
      }
      const gym = GymResolver.getByName(bookingWizardState.currentSelectedGymName);
      const createBookingArguments: IMakeBookingArguments = {
        gymName: gym.name,
        timezone: gym.timezone,
        workoutType,
        startDateUTC: startTime,
        withPartner: false,
        isInstant: true,
        isDemo: false,
        areaCategory,
      };
      const bookingServiceClient = createBookingServiceClient();
      const booking = await bookingServiceClient.makeBooking(createBookingArguments);
      dispatch({ type: types.START_ON_DEMAND_STUDIO_BOOKING_SUCCESS, payload: { booking } });
      dispatch(getNextBookingStep(userProfile));
    } catch (e) {
      const { message } = (e as Error);
      dispatch({ type: types.START_ON_DEMAND_STUDIO_BOOKING_FAILED, payload: { error: message } });
    }
  }
);

export const onDemandCompletedDonePressedAction = (userProfile: UserProfile) => (
  (dispach) => {
    dispach(getNextBookingStep(userProfile));
  }
);

export const setDuration = (workoutType: WorkoutType) => ({
  type: types.SET_DURATION,
  payload: { workoutType },
  ...analyicsForAction('book_home_duration', { duration: getWorkoutDurationDescription[workoutType] }),
});

export const selectStudioPartner = (partner: IDisplayableUserProfile) => ({
  type: types.SELECT_STUDIO_PARTNER,
  payload: { partner },
});

export const makeBooking = (
  workoutSessionService: WorkoutSessionService,
  waitingListServiceClient: WaitingListServiceClient,
  userProfile: UserProfile,
) => async (dispatch, getState) => {
  const state = getState();
  const {
    currentSelectedGymName, isSoloWorkout, pendingSlot,
    recommendedSettings, studioPartner, livestreamOptions, livestreamToggleValue,
    areaCategory,
  } = state.bookings.bookingWizard;

  const withPartner = !isSoloWorkout;
  // TODO: handle studio with partner flow

  try {
    dispatch(makeBookingRequest());
    let createdWorkout: UserWorkoutSession;
    const gym = GymResolver.getByName(currentSelectedGymName);
    if (gym.type === GymTypes.Indoor && withPartner) {
      createdWorkout = await workoutSessionService.bookAndInvite(
        studioPartner.uid,
        pendingSlot,
        currentSelectedGymName,
        recommendedSettings,
        areaCategory,
      );
    } else {
      createdWorkout = await workoutSessionService.makeBooking(
        withPartner,
        pendingSlot,
        currentSelectedGymName,
        recommendedSettings,
        false,
        getConfig().workoutPlaybackSpeed,
        livestreamOptions,
        livestreamToggleValue,
        areaCategory,
      );
    }

    let waitlistEntries: WaitlistEntry[] = [];
    if (!UserWorkoutSession.isHomeBooking(createdWorkout, GymResolver.getByName)) {
      waitlistEntries = await waitingListServiceClient.getWaitingList();
    }
    const bookings = await workoutSessionService.getMyCurrentBookings();
    dispatch(makeBookingSuccess(bookings, waitlistEntries, createdWorkout));

    const isInstantDemoWorkout = createdWorkout && createdWorkout.isDemo && createdWorkout.isInstant;
    if (isInstantDemoWorkout) {
      return dispatch(getNextBookingStep(userProfile, ProcessTypes.StartInstant, undefined, isSoloWorkout));
    }
    dispatch(getNextBookingStep(userProfile, ProcessTypes.InviteToHomeWorkout, undefined, isSoloWorkout));
  } catch (error) {
    Logger.notify(error, (report) => {
      report.errorClass = 'Make booking';
      const errorString = JSON.stringify(error);
      report.errorMessage = `${errorString}timeSlot: ${JSON.stringify(pendingSlot)}`;
    });

    dispatch(makeBookingFailed((error as Error).message));
  }
};

export const acceptInvite = (userWorkoutSession: UserWorkoutSession) => ({
  type: types.ACCEPT_INVITE,
  payload: {
    userWorkoutSession,
  },
});

export const makeBookingRequest = () => ({
  type: types.MAKE_BOOKING_REQUEST,
  ...analyicsForAction(types.MAKE_BOOKING_REQUEST),
});

export const acceptWorkoutSettingsChange = (allWorkoutSessions) => ({
  type: types.ACCEPT_WORKOUT_SETTINGS_CHANGE,
  payload: {
    allWorkoutSessions,
  },
  ...analyicsForAction(types.ACCEPT_WORKOUT_SETTINGS_CHANGE),
});

const gymTypeAnalyticsDescription = (currentSelectedGymName) => {
  if (!currentSelectedGymName) {
    return;
  }
  const gym = GymResolver.getByName(currentSelectedGymName);
  return gym.type === GymTypes.Indoor ? 'studio' : 'home';
};

export const customiseBookingSettingsRequest = () => async (dispatch, getState) => {
  const state = getState();
  const { currentSelectedGymName } = state.bookings.bookingWizard;
  const gymType = gymTypeAnalyticsDescription(currentSelectedGymName);
  dispatch({
    type: types.CUSTOMISE_BOOKINGS_SETTINGS_REQUEST,
    ...analyicsForAction(`book_${gymType}_summary_customise_request`),
  });
};

export const makeBookingSuccess = (
  workoutSessions: UserWorkoutSession[],
  waitlistEntries: WaitlistEntry[],
  bookingJustCreated: UserWorkoutSession,
): IMakeBookingSuccess => ({
  type: types.MAKE_BOOKING_SUCCESS,
  payload: { workoutSessions, waitlistEntries, bookingJustCreated },
}
);

export interface IMakeBookingSuccess {
  type: typeof types.MAKE_BOOKING_SUCCESS;
  payload: {
    workoutSessions: UserWorkoutSession[];
    waitlistEntries: WaitlistEntry[];
    bookingJustCreated: UserWorkoutSession;
  };
}

export const makeBookingFailed = (error: string) => ({
  type: types.MAKE_BOOKING_FAILURE,
  payload: { error },
});

export const getRecommendedSettings = (
  workoutSessionService: WorkoutSessionService,
  userProfile: UserProfile,
  day: IDay,
) => async (dispatch, getState) => (
  fetchAndSaveRecommendedSettings(dispatch, getState, workoutSessionService, userProfile, day)
);

const fetchAndSaveRecommendedSettings = async (dispatch, getState, workoutSessionService, userProfile, day) => {
  const state = getState();
  const currentSelectedGymName = state.bookings.bookingWizard.currentSelectedGymName
    ?? state.homeWorkouts.homeWorkoutSession?.gym?.name;
  try {
    dispatch(getRecommendedSettingsRequest());
    const startOfDay = moment(day.date).startOf('day');
    const recommended: IUserRecommendedSettings = await workoutSessionService.getRecommendedSettingsForDate(
      startOfDay,
      userProfile.longTermGoal,
      currentSelectedGymName,
    );
    dispatch(getRecommendedSettingsSuccess(recommended));
  } catch (error) {
    Logger.notify(error, (report) => {
      report.errorClass = 'Get Recommended settings';
      report.errorMessage = JSON.stringify(error);
    });
    dispatch(getRecommendedSettingsFailed((error as Error).message));
  }
};

export const getRecommendedSettingsRequest = () => ({
  type: types.GET_RECOMMENDED_SETTINGS_REQUEST,
});

const getRecommendedSettingsSuccess = (recommended: IUserRecommendedSettings) => async (dispatch) => {
  dispatch({
    type: types.GET_RECOMMENDED_SETTINGS_SUCCESS,
    payload: { recommended },
  });
};

export const workoutSettingsUpdated = (settings: WorkoutSettings) => async (dispatch, getState) => {
  const state = getState();
  const { currentSelectedGymName } = state.bookings.bookingWizard;
  const gymType = gymTypeAnalyticsDescription(currentSelectedGymName);
  dispatch({
    type: types.UPDATE_SETTINGS,
    payload: { settings },
    ...analyicsForAction(`book_${gymType}_customise_change`),
  });
};

export const getRecommendedSettingsFailed = (error: string) => ({
  type: types.GET_RECOMMENDED_SETTINGS_FAILURE,
  payload: { error },
});

export const changeBooking = (
  workoutSessionService: WorkoutSessionService,
  userProfile: UserProfile,
) => async (dispatch, getState) => {
  const state = getState();
  const {
    currentSelectedGymName, isSoloWorkout, pendingSlot, workoutSession, recommendedSettings,
  } = state.bookings.bookingWizard;

  try {
    dispatch(changeBookingRequest());
    const changedWorkoutSession = await workoutSessionService.changeBooking(
      workoutSession.uniqueReference,
      pendingSlot.startTime,
      pendingSlot.workoutType,
      currentSelectedGymName,
      recommendedSettings,
    );

    dispatch(changeBookingSuccess(workoutSession.uniqueReference, changedWorkoutSession));
    dispatch(getNextBookingStep(userProfile, ProcessTypes.InviteToHomeWorkout, undefined, isSoloWorkout));
  } catch (error) {
    Logger.notify(error, (report) => {
      report.errorClass = 'Change booking';
      const errorString = JSON.stringify(error);
      report.errorMessage = `${errorString}timeSlot: ${JSON.stringify(pendingSlot)}`;
    });
    dispatch(changeBookingFailed((error as Error).message));
  }
};

export const changeBookingRequest = () => ({
  type: types.CHANGE_BOOKING_REQUEST,
});

export const changeBookingSuccess = (
  changedWorkoutId: string,
  workoutSession: UserWorkoutSession,
): IChangeBookingSuccess => ({
  type: types.CHANGE_BOOKING_SUCCESS,
  payload: { workoutSession, changedWorkoutId },
});

export interface IChangeBookingSuccess {
  type: typeof types.CHANGE_BOOKING_SUCCESS;
  payload: {
    workoutSession: UserWorkoutSession;
    changedWorkoutId: string;
  };
}

export const changeBookingFailed = (error: string) => ({
  type: types.CHANGE_BOOKING_FAILURE,
  payload: { error },
});

export const selectSkillLevel = (
  userProfileService,
  userProfile: UserProfile,
  skillLevel: Level,
) => async (dispatch, getState) => {
  try {
    dispatch(selectSkillLevelRequest());
    userProfile.skillLevel = skillLevel;
    await userProfileService.updateUserProfile(userProfile);
    dispatch(selectSkillLevelSuccess());
    dispatch(getNextBookingStep(userProfile, ProcessTypes.Start, undefined));
  } catch (error) {
    Logger.notify(error, (report) => {
      report.errorClass = 'Save skill level';
      report.errorMessage = JSON.stringify(error);
    });

    dispatch(selectSkillLevelFailed((error as Error).message));
  }
};

export const setDefaultEquipment = (
  userServiceClient: UserServiceClient,
  userProfile: BaseUserProfile,
  equipment: Equipment[],
) => async (dispatch) => {
  try {
    userProfile.defaultEquipment = equipment;
    await userServiceClient.updateUser(userProfile);
  } catch (error) {
    Logger.notify(error, (report) => {
      report.errorClass = 'Bookings.setDefaultEquipment';
      report.errorMessage = JSON.stringify(error);
    });
  }
};

export const selectSkillLevelRequest = () => ({
  type: types.SELECT_SKILL_LEVEL_REQUEST,
});

export const selectSkillLevelSuccess = () => ({
  type: types.SELECT_SKILL_LEVEL_SUCCESS,
});

export const selectSkillLevelFailed = (error: string) => ({
  type: types.SELECT_SKILL_LEVEL_FAILURE,
  payload: { error },
});

export const openPersonaliseWorkoutSetupScreen = (
  screen: PersonaliseSetupScreens,
  workoutSettings?: WorkoutSettings,
  gymType?: any,
  allowToSetEquipment?: boolean,
  userprofile?: UserProfile,
) => ({
  type: types.OPEN_PERSONALISE_WORKOUT_SETUP_SCREEN,
  payload: {
    screen,
    workoutSettings,
    gymType,
    allowToSetEquipment,
    userprofile,
  },
});

export const clearPersonaliseSetupWarning = () => ({
  type: types.CLEAR_PERSONALISE_SETUP_WARNING,
});

export const setPersonaliseWorkoutSetupScreen = (screen: PersonaliseSetupScreens) => async (dispatch, getState) => {
  const state = getState();
  const { updatedWorkoutSettings, homeWorkoutSession, userProfile } = state.homeWorkouts;
  let gymType;
  if (homeWorkoutSession && homeWorkoutSession.gym) {
    gymType = homeWorkoutSession.gym.type;
  }
  let allowToSetEquipment: boolean | undefined;
  if (userProfile) {
    allowToSetEquipment = EnabledFeatures.canSelectEquipment(userProfile);
  }
  dispatch(openPersonaliseWorkoutSetupScreen(
    screen,
    updatedWorkoutSettings,
    gymType,
    allowToSetEquipment,
    userProfile,
  ));
};

export const setupDefaultHomeEquipmentNextPressed = (userProfile: UserProfile) => (
  async (dispatch) => {
    dispatch({
      type: types.SETUP_DEFAULT_HOME_EQUIPMENT_NEXT_PRESSED,
      ...analyicsForAction(types.SETUP_DEFAULT_HOME_EQUIPMENT_NEXT_PRESSED),
    });
    dispatch(getNextBookingStep(userProfile));
  }
);

export const enableMirrorPermissionsPressed = (userProfile: UserProfile) => (
  async (dispatch) => {
    dispatch({
      type: types.ENABLE_MIRROR_PERMISSION_PRESSED,
      ...analyicsForAction(types.ENABLE_MIRROR_PERMISSION_PRESSED),
    });
    await Permissions.requestCameraPermission();
    dispatch(getNextBookingStep(userProfile));
  }
);

export const setupMirrorLaterPressed = (userProfile: UserProfile) => (
  async (dispatch) => {
    dispatch({
      type: types.SETUP_MIRROR_LATER_PRESSED,
      ...analyicsForAction(types.SETUP_MIRROR_LATER_PRESSED),
    });
    dispatch(getNextBookingStep(userProfile));
  }
);

export const connectAppleHealthKitSkipPressed = (userProfile: UserProfile) => (
  async (dispatch) => {
    dispatch({
      type: types.CONNECT_APPLE_HEALTH_KIT_SKIP_PRESSED,
      ...analyicsForAction(types.CONNECT_APPLE_HEALTH_KIT_SKIP_PRESSED),
    });
    dispatch(getNextBookingStep(userProfile));
  }
);

export const connectAppleHealthKitNextPressed = (userProfile: UserProfile) => (
  async (dispatch) => {
    dispatch({
      type: types.CONNECT_APPLE_HEALTH_KIT_NEXT_PRESSED,
      ...analyicsForAction(types.CONNECT_APPLE_HEALTH_KIT_NEXT_PRESSED),
    });
    dispatch(getNextBookingStep(userProfile));
  }
);

export const setupHRMonitorNextPressed = (userProfile: UserProfile) => (
  async (dispatch) => {
    dispatch({
      type: types.SETUP_HR_MONITOR_NEXT_PRESSED,
      ...analyicsForAction(types.SETUP_HR_MONITOR_NEXT_PRESSED),
    });
    dispatch(getNextBookingStep(userProfile));
  }
);

export const setupHRMonitorViewSupportedDevicesPressed = () => ({
  type: types.SETUP_HR_MONITOR_VIEW_SUPPORTED_DEVICES_PRESSED,
  ...analyicsForAction(types.SETUP_HR_MONITOR_VIEW_SUPPORTED_DEVICES_PRESSED),
});

export const setupHRMonitorViewSupportedDevicesClosed = () => ({
  type: types.SETUP_HR_MONITOR_VIEW_SUPPORTED_DEVICES_CLOSED,
  ...analyicsForAction(types.SETUP_HR_MONITOR_VIEW_SUPPORTED_DEVICES_CLOSED),
});

export const closeFeatureDemoBookingScreen = (
  bookingServiceClient: BookingServiceClient,
  bookingStep: BookingSteps,
) => (
  async (dispatch, getState: () => IAppStore) => {
    try {
      const { bookings: { bookingWizard } } = getState();
      const deleteBookingId = bookingWizard.workoutSession ? bookingWizard.workoutSession.uniqueReference : null;
      dispatch({
        type: types.CLOSE_FEATURE_DEMO_SCREEN_REQUEST,
        payload: {
          deleteBookingId,
        },
        ...analyicsForAction(types.CLOSE_FEATURE_DEMO_SCREEN_REQUEST, { bookingStep }),
      });
      if (deleteBookingId) {
        await bookingServiceClient.cancelBookingAndReturnBookingList(deleteBookingId);
      }
      dispatch({ type: types.CLOSE_FEATURE_DEMO_SCREEN_SUCCESS });
    } catch {
      dispatch({ type: types.CLOSE_FEATURE_DEMO_SCREEN_FAILURE });
    }
  }
);

interface IOpenBookingWizardParams {
  userProfile: UserProfile;
  dispatch: Dispatch<any>;
  getState: () => IAppStore;
  booking?: UserWorkoutSession;
  dayToBook?: moment.Moment;
  waitingList?: any;
  processType?: ProcessTypes;
  gymName?: string;
  isSoloWorkout?: boolean;
  fromPlanner?: boolean;
}

const openBookingWizardFromParams = (params: IOpenBookingWizardParams) => {
  openBookingWizard(
    params.userProfile,
    params.dispatch,
    params.getState,
    params.booking,
    params.dayToBook,
    params.waitingList,
    params.processType,
    params.gymName,
    params.isSoloWorkout,
    params.fromPlanner,
  );
};

const openBookingWizard = (
  userProfile: UserProfile,
  dispatch: Dispatch<any>,
  getState: () => IAppStore,
  booking?: UserWorkoutSession,
  dayToBook?: moment.Moment,
  waitingList?: any,
  processType?: ProcessTypes,
  gymName?: string,
  isSoloWorkout?: boolean,
  fromPlanner?: boolean,
) => {
  dispatch(resetWizard(userProfile));
  dispatch(toggleBookingsModal(false));

  let day = buildDayForBooking(moment(), true);
  if (dayToBook) {
    const isToday = moment(dayToBook).isSame(moment(), 'days');
    const defaultTimeForFutureBookings = 6; // 6am
    const startTimeForEmptySlot = isToday
      ? moment()
      : moment(dayToBook).startOf('day').add(defaultTimeForFutureBookings, 'hours');
    const dayForBookingWizard = booking ? moment(booking.startTimeOfFirstExercise) : startTimeForEmptySlot;
    day = buildDayForBooking(dayForBookingWizard, isToday);
  }
  dispatch(setDayTemp(day));

  if (gymName) {
    dispatch(setSelectedGym(gymName));
  }

  if (isSoloWorkout !== undefined) {
    dispatch(setSoloWorkout(isSoloWorkout));
  }

  const state = getState();
  const { workoutSessions } = state.planner;
  const { totalStartedWorkouts } = state.summaryDashboard.data;
  const isFirstWorkout = totalStartedWorkouts === 0;

  const invites = filterInvitesForDay(workoutSessions, day);

  const waitlistEntry = filterWaitingListForDay(waitingList || [], day);

  dispatch(setWorkoutSession(booking));
  dispatch(setInvites(invites));
  dispatch(setWaitlistEntry(waitlistEntry));

  const initialStartingProcess = processType ?? ProcessTypes.Start;
  dispatch(getNextBookingStep(
    userProfile,
    initialStartingProcess,
    workoutSessions,
    undefined,
    fromPlanner,
    isFirstWorkout,
  ));

  setTimeout(() => {
    dispatch(toggleBookingsModal(true));
  }, 0);
};

const closeBookingWizard = (dispatch, userProfile) => {
  dispatch(resetWizard(userProfile));
  dispatch(toggleBookingsModal(false));
};

export const createDemoStudioBookingOpened = (userProfile, navigate) => (
  async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
    try {
      dispatch({ type: types.CREATE_DEMO_STUDIO_SCREEN_OPENED_REQUEST });

      const state = getState();
      const deepLinkedEmail = state.deepLinkParamaters.email;
      const notLinkedToAppWithCurrentAuthedUser = deepLinkedEmail && userProfile.email !== deepLinkedEmail;

      const { gymName } = state.deepLinkParamaters;

      const hasRequiredDeepLinkParams = !!gymName;
      if (!hasRequiredDeepLinkParams) {
        return navigate('HomeLoading');
      }

      const userServiceClient = createUserServiceClient();
      const { allowDemoBookingsForGym } = await userServiceClient.getAllowDemoBookingsForGym(gymName);

      dispatch({
        type: types.CREATE_DEMO_STUDIO_SCREEN_OPENED_SUCCESS,
        payload: { allowDemoBookingsForGym, notLinkedToAppWithCurrentAuthedUser },
      });

      const newState = getState();
      if (newState.bookings.demo.shouldOpenDemoBookingWizard) {
        openBookingWizard(
          userProfile,
          dispatch,
          getState,
          undefined,
          null,
          null,
          ProcessTypes.DemoStudioBooking,
          gymName,
          true,
        );
      }
    } catch (error) {
      dispatch({ type: types.CREATE_DEMO_STUDIO_SCREEN_OPENED_FAILURE, payload: { error } });
    }
  }
);

export const openBookingWizardForInviteBooking = (userProfile, timeToBook) => (dispatch, getState) => {
  const time = moment.unix(timeToBook);
  openBookingWizard(userProfile, dispatch, getState, undefined, time);
};

export const closeDemoBookingSummaryScreen = (userProfile: UserProfile, gymName: string) => (
  async (dispatch) => {
    dispatch({
      type: types.CLOSE_DEMO_BOOKING_SUMMARY_SCREEN,
      ...analyicsForAction(types.CLOSE_DEMO_BOOKING_SUMMARY_SCREEN),
    });
    notifyParentsOfCompletion();
    await openGymWebpage(userProfile, gymName);
  }
);

export const setupAppPressed = (userProfile: UserProfile) => async (dispatch) => {
  dispatch({
    type: types.SETUP_APP_PRESSED,
    payload: {
      shouldNavigateToInstallAppScreen: isDesktop(),
    },
    ...analyicsForAction(types.SETUP_APP_PRESSED),
  });

  setTimeout(() => dispatch({ type: types.SETUP_APP_PRESSED_SUCCESS }), 3000);

  if (isDesktop()) {
    return closeBookingWizard(dispatch, userProfile);
  }

  if (userProfile.continueSignupLink) {
    return openLink(userProfile.continueSignupLink);
  }
  return openLink(getConfig().appDownloadUrl);
};

export const removeWaitlistEntry = (userProfile: UserProfile) => async (dispatch, getState: () => IAppStore) => {
  const state = getState();
  const enabledFeatures = enabledFeaturesSelector(state);
  dispatch({
    type: types.REMOVE_WAITLIST_ENTRY,
    payload: {
      userProfile,
      enabledFeatures,
    },
  });
};

export const confirmRemoveWaitlistEntry = (waitlistEntry) => async (dispatch) => {
  dispatch({
    type: types.CONFIRM_REMOVE_WAITLIST_ENTRY,
    payload: {
      waitlistEntry,
    },
  });
};

export const bookWaitlistSlot = (
  workoutSessionService: WorkoutSessionService,
  userProfile: UserProfile,
  waitlistEntry,
) => async (dispatch) => {
  dispatch(waitlistBookingRequest());
  try {
    const day = buildDayForBooking(moment(waitlistEntry.timeSlot.startTime), /* isToday */ false);
    dispatch(getRecommendedSettings(workoutSessionService, userProfile, day));
    dispatch(waitlistBookingSuccess(userProfile));
  } catch (error) {
    dispatch(waitlistBookingFailure(error));
  }
};

export const startWorkoutNowButtonInBookingWizardPressed = (
  workoutSessionService: WorkoutSessionService,
  waitingListServiceClient: WaitingListServiceClient,
  userProfile: UserProfile,
) => async (dispatch, getState: () => IAppStore) => {
  try {
    const {
      currentSelectedGymName,
    } = getState().bookings.bookingWizard;
    const currentGym = GymResolver.getByName(currentSelectedGymName);
    if (!currentGym) {
      throw new Error(`no gym found for ${currentSelectedGymName}`);
    }
    dispatch({ type: types.START_WORKOUT_NOW_REQUEST, payload: { gymName: currentGym.name } });

    const day = buildDayForBooking(moment(), true /* isToday */);
    await Promise.all([
      getBookingSlots(dispatch, getState, workoutSessionService, day, currentGym),
      fetchAndSaveRecommendedSettings(dispatch, getState, workoutSessionService, userProfile, day),
    ]);

    const updatedState = getState();
    const {
      pendingSlot,
      recommendedSettings,
    } = updatedState.bookings.bookingWizard;

    const createdWorkout = await workoutSessionService.makeBooking(
      false, /* withPartner */
      pendingSlot,
      currentSelectedGymName,
      recommendedSettings,
      getConfig().workoutPlaybackSpeed,
    );
    const bookings = await workoutSessionService.getMyCurrentBookings();
    dispatch(makeBookingSuccess(bookings, [], createdWorkout));

    dispatch(getNextBookingStep(userProfile, ProcessTypes.StartInstant, undefined, true));

    dispatch({ type: types.START_WORKOUT_NOW_SUCCESS });
  } catch (error) {
    dispatch({ type: types.START_WORKOUT_NOW_FAILURE, payload: { error } });
  }
};

export const scheduleOrStartWorkoutButtonPressedAction = {
  type: types.SCHEDULE_OR_START_WORKOUT_BUTTON_PRESSED,
};

const openedPaywallBecauseAccountInactive = (
  dispatch,
  userProfile: UserProfile,
): boolean => {
  if (!UserProfile.hasActivePlan(userProfile)) {
    dispatch(openPaywallModalAction(PaywallPage.Workout));
    new GoogleAnalytics().logEventWithParamaters('paywall_bookings');
    return true;
  }
  return false;
};

export const bookWorkoutSliderCardPressed = (userProfile) => ((dispatch, getState) => {
  if (openedPaywallBecauseAccountInactive(dispatch, userProfile)) {
    return;
  }
  openBookingWizard(userProfile, dispatch, getState);
  dispatch({
    type: types.BOOK_WORKOUT_SLIDER_CARD_PRESSED,
    ...analyicsForAction(types.BOOK_WORKOUT_SLIDER_CARD_PRESSED),
  });
});

export const checkinToStudioWorkout = (userProfile, workoutSession) => (
  async (dispatch, getState) => {
    const bookingDay = moment.unix(workoutSession.startTimeOfFirstExercise);
    openBookingWizard(
      userProfile,
      dispatch,
      getState,
      workoutSession,
      bookingDay,
      undefined,
      ProcessTypes.CheckIn,
    );
    await checkUserIn(dispatch, workoutSession);
  }
);

export interface ISaveBagContactDuringCheckInSuccess {
  type: typeof types.UPDATING_BOXING_SETTINGS_DURING_CHECKIN_SUCCESS;
  payload: {
    updatedWorkout?: UserWorkoutSession;
  }
}

export const saveBagContactSettingsAndCheckIn = (userProfile: UserProfile, allowBagContact: boolean) => (
  async (dispatch, getState: () => IAppStore) => {
    try {
      dispatch({ type: types.UPDATING_BOXING_SETTINGS_DURING_CHECKIN_REQUEST });
      const { bookings: { bookingWizard: { workoutSession } } } = getState();
      const checkedInSession = await checkUserIn(dispatch, workoutSession);

      let updatedWorkout;
      if (workoutSession && !!workoutSession.settings.allowBagContact !== allowBagContact) {
        const newWorkoutSettings: WorkoutSettings = { ...workoutSession.settings, allowBagContact };
        await updateWorkoutSettings(workoutSession.uniqueReference, newWorkoutSettings);
        updatedWorkout = { ...checkedInSession, settings: newWorkoutSettings };
        dispatch(setWorkoutSession(updatedWorkout));
      }
      dispatch(getNextBookingStep(userProfile, ProcessTypes.CheckIn));
      dispatch({ type: types.UPDATING_BOXING_SETTINGS_DURING_CHECKIN_SUCCESS, payload: { updatedWorkout } });
    } catch {
      const errorMessage = 'Failed to set boxing setting for booking. Please try again';
      dispatch({ type: types.UPDATING_BOXING_SETTINGS_DURING_CHECKIN_FAILURE, payload: { errorMessage } });
    }
  });

export const openBagContactStep = (userProfile, workoutSession) => (
  async (dispatch, getState) => {
    const bookingDay = moment.unix(workoutSession.startTimeOfFirstExercise);
    openBookingWizard(
      userProfile,
      dispatch,
      getState,
      workoutSession,
      bookingDay,
      undefined,
      ProcessTypes.BagContact,
    );
  }
);

export const openBookingSettings = (userProfile, workoutSession) => (
  async (dispatch, getState) => {
    const bookingDay = moment.unix(workoutSession.startTimeOfFirstExercise);
    openBookingWizard(
      userProfile,
      dispatch,
      getState,
      workoutSession,
      bookingDay,
      undefined,
      ProcessTypes.Start,
    );
  }
);

const reloadBookingIfGroupWorkout = async (workoutSession: UserWorkoutSession, dispatch: Dispatch<any>) => {
  if (UserWorkoutSession.hasPairedWorkoutSession(workoutSession)) {
    const bookingServiceClient = createBookingServiceClient();
    const reloadedSession = await bookingServiceClient.getWorkoutSession(workoutSession.uniqueReference);
    dispatch(setWorkoutSession(reloadedSession));
    dispatch(getOpenInviteClassList(bookingServiceClient, reloadedSession.uniqueReference, true));
    dispatch({
      type: PARTNER_BOOKING_UPDATED,
      payload: {
        workout: reloadedSession,
      },
    });
  }
};

export const workoutBookingSliderCardPressed = (userProfile, workoutSession) => (
  async (dispatch, getState) => {
    if (openedPaywallBecauseAccountInactive(dispatch, userProfile)) {
      return;
    }

    const bookingDay = moment.unix(workoutSession.startTimeOfFirstExercise);
    openBookingWizard(userProfile, dispatch, getState, workoutSession, bookingDay);
    dispatch({
      type: types.WORKOUT_BOOKING_SLIDER_CARD_PRESSED,
      ...analyicsForAction(types.WORKOUT_BOOKING_SLIDER_CARD_PRESSED),
    });
    await reloadBookingIfGroupWorkout(workoutSession, dispatch);
  }
);

export const influencerBookingCardPressed = (userProfile, workoutSession, openBookings = false) => (
  async (dispatch, getState) => {
    const bookingTime = new Date(workoutSession.startTimeOfFirstExercise).getTime();
    const inviteId = workoutSession.uniqueReference;
    dispatch(influencerScheduleTilePressed(bookingTime, inviteId, workoutSession.creatorName));
    if (openBookings) {
      dispatch(openBookingWizardForInviteBooking(userProfile, bookingTime));
    }
  }
);

const openBookingWizardWithWaitlistEntry = (waitlistEntry, userProfile, dispatch) => {
  dispatch(resetWizard(userProfile));
  dispatch(setWaitlistEntry(waitlistEntry));
  dispatch(getNextBookingStep(userProfile, ProcessTypes.Start));
  dispatch(toggleBookingsModal(true));
};

export const waitlistEntrySliderCardPressed = (userProfile, waitlistEntry) => (
  (dispatch) => {
    if (openedPaywallBecauseAccountInactive(dispatch, userProfile)) {
      return;
    }
    openBookingWizardWithWaitlistEntry(waitlistEntry, userProfile, dispatch);
    dispatch({
      type: types.WAITLIST_SLIDER_CARD_PRESSED,
      ...analyicsForAction(types.WAITLIST_SLIDER_CARD_PRESSED),
    });
  }
);

async function checkUserIn(dispatch: any, workoutSession: any) {
  try {
    dispatch({ type: types.CHECKIN_REQUEST });
    const bookingServiceClient = createBookingServiceClient();
    const checkedInWorkoutSession: UserWorkoutSession = await bookingServiceClient.checkIn(workoutSession.gymName);
    dispatch({
      type: types.CHECKIN_SUCCESS,
      payload: {
        changedWorkoutId: checkedInWorkoutSession.uniqueReference,
        workoutSession: checkedInWorkoutSession,
      },
    });
    await startStudioWatchAppWorkout(checkedInWorkoutSession, dispatch);
    return checkedInWorkoutSession;
  } catch (error) {
    dispatch({ type: types.CHECKIN_FAILURE, payload: { errorMessage: (error as Error).message } });
  }
}

function notifyParentsOfCompletion() {
  if (!window.parent) {
    return;
  }
  try {
    window.parent.postMessage('free-booking-complete', '*');
  } catch (e) {
    // ignore
  }
}

export const continueDemoBookingPressedAction = (userProfile: UserProfile) => (
  async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
    dispatch({ type: types.CONTINUE_DEMO_BOOKING_PRESSED });
    const newState = getState();
    const { gymName } = newState.deepLinkParamaters;
    if (newState.bookings.demo.shouldOpenDemoBookingWizard) {
      openBookingWizard(
        userProfile,
        dispatch,
        getState,
        undefined,
        null,
        null,
        ProcessTypes.DemoStudioBooking,
        gymName,
        true,
      );
    }
  }
);

export const workoutTypeChanged = (cardioOrResistance: CardioOrResistance) => (dispatch: any) => {
  dispatch({
    type: types.WORKOUT_TYPE_SELECTED_IN_BOOKING_STEP,
    payload: {
      cardioOrResistance,
    },
  });
};

export const plannerDayPressedAction = (
  userProfile: UserProfile,
  dayToBook: moment.Moment,
) => (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  if (!openedPaywallBecauseAccountInactive(dispatch, userProfile)) {
    openBookingWizardFromParams({
      userProfile, dispatch, getState, dayToBook, fromPlanner: true,
    });
  }
};

export const plannerEntryPressedAction = (
  userProfile: UserProfile,
  booking: UserWorkoutSession,
) => async (dispatch: Dispatch<any>, getState: () => IAppStore) => {
  if (!openedPaywallBecauseAccountInactive(dispatch, userProfile)) {
    const dayToBook = moment.unix(booking.startTimeOfFirstExercise);
    openBookingWizardFromParams({
      userProfile, dispatch, getState, booking, dayToBook,
    });
    await reloadBookingIfGroupWorkout(booking, dispatch);
  }
};
